import {
    ActionStateValues,
    ButtonType,
    ColorsType,
    TheWestColourMap,
    tokens,
} from '@news-mono/design-tokens'
import { Product } from '@news-mono/web-common'
import { FallbackImages } from '../__fallback-images'
import { Fonts, fonts } from '../__styling/settings/fonts'
import {
    CardMargins,
    ComponentMetrics,
    metrics,
    SiteMetrics,
    ThemeMargins,
} from '../__styling/settings/metrics'
import { sections } from './settings/sections'
import { PerthNowSectionValues } from './settings/sections/perthnow'
import { SevenNewsSectionValues } from './settings/sections/sevennews'
import { TheWestSectionValues } from './settings/sections/thewest'
import {
    CommonZIndex,
    PerthNowZindex,
    SevenNewsZindex,
    TheWestZindex,
    zIndex,
} from './settings/z-index'

export interface Theme {
    fallbackImages: FallbackImages
    kind: Product
    fonts: Fonts
    margins: ThemeMargins
    cards: CardMargins
    componentMetrics: ComponentMetrics
    siteMetrics: SiteMetrics
    section: string
    sectionValues:
        | TheWestSectionValues
        | PerthNowSectionValues
        | SevenNewsSectionValues
    zIndex: (TheWestZindex | PerthNowZindex | SevenNewsZindex) & CommonZIndex
    baseFontSize: number
    colors: ColorsType
}

type EmotionTheme = Theme

declare module '@emotion/react' {
    export interface Theme extends EmotionTheme {}
}

export const thewest: Theme = {
    kind: Product.TheWest,
    fonts: fonts.thewest,
    margins: metrics.thewest.margins,
    cards: metrics.thewest.cards,
    componentMetrics: metrics.thewest.componentMetrics,
    siteMetrics: metrics.thewest.siteMetrics,
    zIndex: { ...zIndex.thewest, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.thewest.default,
    baseFontSize: metrics.thewest.baseFontSize,
    colors: tokens.thewest.colors,
}

export const perthnow: Theme = {
    kind: Product.PerthNow,
    fonts: fonts.perthnow,
    margins: metrics.perthnow.margins,
    cards: metrics.perthnow.cards,
    componentMetrics: metrics.perthnow.componentMetrics,
    siteMetrics: metrics.perthnow.siteMetrics,
    zIndex: { ...zIndex.perthnow, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.perthnow.default,
    baseFontSize: metrics.perthnow.baseFontSize,
    colors: tokens.perthnow.colors,
}

export const sevennews: Theme = {
    kind: Product.SevenNews,
    fonts: fonts.sevennews,
    margins: metrics.sevennews.margins,
    cards: metrics.sevennews.cards,
    componentMetrics: metrics.sevennews.componentMetrics,
    siteMetrics: metrics.sevennews.siteMetrics,
    zIndex: { ...zIndex.sevennews, ...zIndex.common },

    // This following properties are provided through route resolution in provide-route-resolved-data-to-theme.tsx
    fallbackImages: undefined as any,
    section: 'default',
    sectionValues: sections.sevennews.default,
    baseFontSize: metrics.sevennews.baseFontSize,
    colors: tokens.sevennews.colors,
}

export const themes = [thewest, perthnow, sevennews]

export interface ThemeProps {
    theme: Theme
}

export type ThemeOverrideTypes = 'tokyo'

export function getThemeOverrideValues(override: ThemeOverrideTypes) {
    switch (override) {
        case 'tokyo':
            return getTokyoThemeValues()
        default:
            return {}
    }
}

interface OlympicsTheme {
    sectionValues: any
    colors: {
        background: {
            primary: string
            secondary: string
        }
        brand: string
        text: any
        actions: {
            button: ButtonType
            link: ActionStateValues
        }
        borders: any
        icons: {
            default: string
            hover: string
        }
    }
}

function getTokyoThemeValues(): OlympicsTheme {
    const theme = tokens.thewest.colors as TheWestColourMap
    const palette = theme.palette.olympics
    return {
        sectionValues: { primaryColor: palette.yellow },
        colors: {
            background: {
                primary: palette.white,
                secondary: palette.green,
            },
            brand: palette.yellow,
            text: {
                primary: palette.white,
                secondary: palette.yellow,
                tertiary: palette.green,
                heading: palette.white,
            },
            actions: {
                button: {
                    primary: {
                        background: {
                            default: palette.yellow,
                            hover: palette.green,
                            active: palette.green,
                            focus: palette.green,
                        },
                        text: {
                            default: palette.green,
                            hover: palette.yellow,
                            active: palette.yellow,
                            focus: palette.yellow,
                        },
                        border: {
                            default: palette.yellow,
                            hover: palette.green,
                            active: palette.green,
                            focus: palette.green,
                        },
                    },
                    secondary: {
                        background: {
                            default: palette.green,
                            hover: palette.white,
                            active: palette.yellow,
                            focus: palette.yellow,
                        },
                        text: {
                            default: palette.white,
                            hover: palette.green,
                            active: palette.green,
                            focus: palette.green,
                        },
                        border: {
                            default: palette.green,
                            hover: palette.white,
                            active: palette.yellow,
                            focus: palette.yellow,
                        },
                    },
                },
                link: {
                    default: palette.white,
                    active: palette.yellow,
                    focus: palette.white,
                    hover: palette.yellow,
                },
            },
            borders: {
                primary: palette.border,
                secondary: palette.border,
            },
            icons: {
                default: palette.yellow,
                hover: palette.white,
            },
        },
    }
}
